
import React, { useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { 
  getPaymentBackup, 
  recoverSubscriptionFromPayment, 
  clearPaymentBackup,
  hasAttemptedRecovery,
  markRecoveryAttempted 
} from '@/utils/PaymentHelper';

export function SubscriptionManager() {
  const { subscription, isLoading, refreshSubscription } = useSubscription();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  
  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await refreshSubscription();
      // Only show success toast if actively refreshing, not on initial load
      toast.success("Subscription status updated");
    } catch (error) {
      console.error("Error refreshing subscription:", error);
      toast.error("Failed to refresh subscription status");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRecoveryAttempt = async () => {
    // Prevent duplicate recovery attempts
    if (hasAttemptedRecovery()) {
      toast.info("Recovery already attempted in this session");
      return;
    }
    
    // Prevent recovery if already subscribed
    if (subscription?.has_active_subscription) {
      toast.info("You already have an active subscription");
      return;
    }
    
    setIsRecovering(true);
    try {
      markRecoveryAttempted();
      const backup = getPaymentBackup();
      
      if (backup && (backup.paymentId || backup.orderId)) {
        // Use quiet mode (no toasts) for recovery to prevent duplication
        const success = await recoverSubscriptionFromPayment(backup.paymentId, backup.orderId, false);
        
        if (success) {
          await refreshSubscription();
          // Only show toast here in the recovery flow to avoid duplicates
          toast.success("Subscription successfully recovered!");
        } else {
          toast.error("Could not recover subscription automatically. Please contact support.");
          clearPaymentBackup();
        }
      } else {
        toast.error("No payment records found for recovery");
      }
    } catch (error) {
      console.error("Error recovering subscription:", error);
      toast.error("Failed to recover subscription");
    } finally {
      setIsRecovering(false);
    }
  };
  
  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription>Your current subscription status</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }
  
  // Check if user has a subscription
  const hasSubscription = subscription?.has_active_subscription;
  const subscriptionDetails = subscription?.subscription;
  const isTrialActive = subscriptionDetails?.is_trial;
  
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Subscription</CardTitle>
            <CardDescription>Your current subscription status</CardDescription>
          </div>
          {hasSubscription ? (
            <Badge variant="outline" className={`${isTrialActive ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
              {isTrialActive ? 'Trial' : 'Active'}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
              Inactive
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {hasSubscription ? (
          <div className="space-y-4">
            <div className={`rounded-lg ${isTrialActive ? 'bg-amber-50' : 'bg-muted'} p-4`}>
              <div className="flex items-center">
                <CheckCircle2 className={`h-5 w-5 ${isTrialActive ? 'text-amber-500' : 'text-green-500'} mr-2`} />
                <h3 className="font-medium">{isTrialActive ? 'Trial Subscription' : 'Active Subscription'}</h3>
              </div>
              <div className="mt-2 text-sm space-y-1">
                <p>Plan: {subscriptionDetails?.plan || 'Premium'}</p>
                {subscriptionDetails?.is_trial && (
                  <p className="text-amber-700 font-medium">
                    Trial expires: {
                      subscriptionDetails.trial_ends_at ? 
                      new Date(subscriptionDetails.trial_ends_at).toLocaleDateString() : 
                      'Unknown'
                    }
                  </p>
                )}
                <p className={`${isTrialActive ? 'text-amber-600' : ''}`}>
                  {isTrialActive ? 'Expires' : 'Renews'}: {
                    subscriptionDetails?.current_period_end ?
                    new Date(subscriptionDetails.current_period_end).toLocaleDateString() :
                    'Unknown'
                  }
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-amber-50 p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                <h3 className="font-medium">No Active Subscription</h3>
              </div>
              <p className="mt-2 text-sm">
                You don't have an active subscription. Subscribe to access premium features.
              </p>
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p>
                If you recently made a payment and your subscription isn't showing up, 
                try refreshing your status or use the recovery option below.
              </p>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={handleRefresh} 
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Status
            </>
          )}
        </Button>
        
        {!hasSubscription && (
          <Button 
            variant="secondary" 
            onClick={handleRecoveryAttempt} 
            disabled={isRecovering || hasAttemptedRecovery()}
          >
            {isRecovering ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recovering...
              </>
            ) : hasAttemptedRecovery() ? (
              "Already Attempted"
            ) : (
              "Recover Subscription"
            )}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
